/**
 * 전장·그리드·화면 레이아웃 파라미터 — 벽돌 그리드/골드 벽돌/멜로디/전투/모듈 그리드/레이아웃.
 * TSV 유래 값(global.tsv·shop.tsv)과 디자인 해상도(390×844) 고정 좌표가 섞여 있다 —
 * 코드 상수는 레이아웃 파생 좌표(SSoT 주석 참조), 수치 밸런스는 TSV에서 수정 후 `npm run export:data`.
 * global.tsv는 long format → {@link globalRepo}로 조회(키 상수 {@link K}).
 */
import type { SafeAreaInsets } from "@yjsgames/safe-area";
import { globalRepo as r, K } from "../globalRepo.js";

/** 벽돌 그리드 배치 파라미터 (디자인 해상도 390×844 기준, 상단 HUD 아래 전투 필드). */
export const GRID = {
  cols: 6,
  rows: 5,
  brickWidth: 48,
  brickHeight: 26,
  hGap: 12,
  vGap: 8,
  fieldTop: 64,
  /**
   * seed 절차적 벽돌 그리드 (ChapterBrickLayout) — chapter 1부터 모든 챕터에 적용.
   * 행 수는 [seedMinRows, seedMaxRows]에서 seed로 선택. seedMaxRows=6이면 하단 ~260px로
   * launcherY(386)보다 충분히 위라 발사 공간 확보 (세로 오버플로 방지).
   */
  seedMinRows: 3,
  seedMaxRows: 6,
  /** 빈 칸에서 블록 배치를 시도할 확률 (0~1). 점유율(밀도) 조절. */
  seedDensity: 0.6,
  /** 총 점유 칸 수 예산(= 총 hpMultiplier 합). 멀티셀 블록의 칸 수 누계가 이를 넘으면 배치 중단. */
  seedHpBudget: 30,
  /**
   * 멀티셀 블록 크기 가중치 (global.tsv) — weightedRoll로 1×1/2×1(가로)/1×2(세로)/2×2 중 추첨.
   * 칸이 모자라면 1×1 fallback. hpMultiplier = 칸수라 큰 블록일수록 HP/보상 비례 증가.
   */
  blockWeights: {
    single: r.getFloat(K.blockWeight1x1),
    horz2: r.getFloat(K.blockWeight2x1),
    vert2: r.getFloat(K.blockWeight1x2),
    square2: r.getFloat(K.blockWeight2x2),
  },
  /**
   * 초반 완충 챕터. 전투 시간 압박 규칙이 붙은 뒤에도 1~2챕터는 업그레이드 없이 방치 클리어가 가능해야 한다.
   * maxChapter 이후는 기존 seedHpBudget/성장 곡선을 그대로 사용한다.
   */
  starterChapter: {
    maxChapter: r.getInt(K.starterChapterMax),
    rampEndChapter: r.getInt(K.starterChapterRampEnd),
    brickHpMultiplier: r.getFloat(K.starterChapterBrickHpMultiplier),
    seedMinRows: r.getInt(K.starterChapterMinRows),
    seedMaxRows: r.getInt(K.starterChapterMaxRows),
    seedDensity: r.getFloat(K.starterChapterDensity),
    seedHpBudget: r.getInt(K.starterChapterHpBudget),
  },
} as const;

/**
 * 레거시 골드 벽돌 설정 (global.tsv SSoT). 현재 런타임 생성 체인에서는 사용하지 않는다.
 * 보상 상자는 mystery 하나만 생성하고, 골드/다이아/유물 보상은 MysteryBlockController가 처리한다.
 */
export const GOLD_BRICK = {
  /** 레거시 셀별 골드 마킹 확률(0~1). 현재 기본값 0. */
  chance: r.getFloat(K.goldBrickChance),
  /** 레거시 챕터당 골드 벽돌 최대 개수. 현재 기본값 0. */
  maxPerChapter: r.getInt(K.goldBrickMaxPerChapter),
  /** 골드 벽돌 처치 보상 골드 배수. */
  rewardMultiplier: r.getFloat(K.goldBrickRewardMultiplier),
  /** 골드 벽돌 고정 색상(hex). */
  tint: r.getString(K.goldBrickTint),
} as const;

/**
 * 미스터리(?) 벽돌 스폰/보상/색 (global.tsv SSoT). 일반 챕터의 normal 셀 일부를 mystery로 마킹한다
 * (markMysteryBricks). 깰 때 골드 배수(값·가중치 데이터 추첨) +
 * 가끔 다이아·유물(각 일일 제한 — MysteryBlockController). HP는 일반 동일(BRICK_KIND_DEFAULT_MULTIPLIER mystery=1).
 * ⚠ 모든 수치는 global.tsv 컬럼이 SSoT — 코드 리터럴/매직넘버 0.
 */
export const MYSTERY_BRICK = {
  /** 셀별 미스터리 마킹 확률(0~1). */
  chance: r.getFloat(K.mysteryBrickChance),
  /** 챕터당 미스터리 벽돌 최대 개수. */
  maxPerChapter: r.getInt(K.mysteryBrickMaxPerChapter),
  /** 미스터리 벽돌 고정 색상(hex) — pickBrickTexture가 fixed tint로 사용. */
  tint: r.getString(K.mysteryBrickTint),
  /**
   * 골드 배수 추첨 엔트리 (값,가중치) — weightedRoll 직접 주입(리터럴 0).
   * mysteryMulValues(int[]) ↔ mysteryMulWeights(float[]) 동일 인덱스 zip (global.tsv 두 array 컬럼).
   */
  goldMultipliers: r.getIntArray(K.mysteryMulValues).map((value, i) => ({
    value,
    weight: r.getFloatArray(K.mysteryMulWeights)[i]!,
  })),
  /** 파괴 시 다이아 지급 확률(0~1, "가끔"). */
  diamondChance: r.getFloat(K.mysteryDiamondChance),
  /**
   * 다이아 지급량 추첨 엔트리 (값,가중치) — weightedRoll 직접 주입(리터럴 0).
   * mysteryDiamondValues(int[]) ↔ mysteryDiamondWeights(float[]) 동일 인덱스 zip.
   */
  diamondTiers: r.getIntArray(K.mysteryDiamondValues).map((value, i) => ({
    value,
    weight: r.getFloatArray(K.mysteryDiamondWeights)[i]!,
  })),
  /** 하루 다이아 지급 최대 횟수(자정 리셋). */
  diamondDailyLimit: r.getInt(K.mysteryDiamondDailyLimit),
  /** 파괴 시 유물 지급 확률(0~1). 현재 기본값 0으로 비활성. */
  relicChance: r.getFloat(K.mysteryRelicChance),
  /** 하루 유물 지급 최대 횟수(자정 리셋). 현재 기본값 0으로 비활성. */
  relicDailyLimit: r.getInt(K.mysteryRelicDailyLimit),
  /** 깰 때 배수 토스트 텍스트 색(hex). */
  toastColor: r.getString(K.mysteryToastColor),
} as const;

/**
 * 멜로디 블록 챕터 (global.tsv SSoT). 매 every 챕터마다 절차 생성 대신 곡 음이 배치된 챕터를
 * 끼워넣는다(resolveChapterLayout). hpBudget은 seedHpBudget과 동일값이라 총 체력 곡선을 보존
 * (총 HP = computeBrickHp(chapter) × hpBudget). staggerMs는 동시 다중 파괴 음 아르페지오 간격(0=즉시, 2차용).
 */
export const MELODY = {
  every: r.getInt(K.melodyEvery),
  hpBudget: r.getInt(K.melodyHpBudget),
  staggerMs: r.getInt(K.melodyStaggerMs),
} as const;

/**
 * 같은 블록 연타 콤보 음정 (global.tsv SSoT). windowMs 이내 재타격마다 콤보가 누적되어
 * 강도 음계 비프가 콤보당 semitoneStep 반음씩 상승(maxSemitone 상한). brickCombo.ts 순수 함수가 소비.
 */
export const BRICK_COMBO = {
  windowMs: r.getInt(K.brickComboWindowMs),
  semitoneStep: r.getInt(K.brickComboSemitoneStep),
  maxSemitone: r.getInt(K.brickComboMaxSemitone),
} as const;

/** 일반 전투 시간 압박 — 주기적으로 벽돌 한 줄을 내리고, 발사대 접촉 시 패배 처리한다. */
export const BATTLEFIELD_PRESSURE = {
  dropIntervalMs: r.getInt(K.battlefieldPressureDropIntervalMs),
  dropRows: r.getInt(K.battlefieldPressureDropRows),
  warningRows: r.getInt(K.battlefieldPressureWarningRows),
  dangerRows: r.getInt(K.battlefieldPressureDangerRows),
} as const;

/** 전투 영역 파라미터. */
export const COMBAT = {
  /** 발사 원점 y (전투 필드 하단 중앙 런처). 이 아래는 탭 콘텐츠 영역. = 게임뷰 interior bottom + 2 (= tabContentTop-10-14+2 = 386). */
  launcherY: 386,
  /** 포신(launcher_barrel) 회전축→포구 끝 거리(px). 발사체 시작·머즐 플래시를 포구에 정렬(muzzleTip). 포신 텍스처(48×48, 축 24 → 포구 ~41)와 정합. */
  launcherMuzzleLength: 17,
  /** 발사체 반지름(px). */
  ballRadius: 6,
  /** 발사체 동시 생성 상한 (split 폭주 방지). */
  maxProjectilesPerFire: 8,
  /** 다발 발사 시 타겟 방향 기준 발사체 간 부채꼴 각도(도). */
  spreadAngleDeg: 12,
  /**
   * 조준 타겟 hysteresis 마진(px). 두 벽돌이 등거리/거의 등거리일 때 포신이 A↔B 떠는 것을 방지 —
   * 현재 조준 타겟보다 이 거리 이상 더 가까운 벽돌이 나타날 때만 전환한다(selectAimTarget).
   * 벽돌 세로 셀 피치(brickHeight26+vGap8=34)의 ~1/3 — 미세 진동은 흡수, 명백히 한 칸 더 가까운 벽돌로는 전환.
   */
  aimStickyMarginPx: 12,
  /**
   * 발사체 하드캡 안전망(ms) — 어떤 이유로든(속도 0/궤도 순환) 벽·벽돌에 닿지 않는 공이 영원히 살아
   * 렉을 유발하는 것을 막는 최후 방어선. 정상 공은 반사 횟수 소진→벽 접촉/벽돌 충돌로 그 전에 소멸하므로
   * 거의 발동하지 않는다. MAX_PROJECTILE_SPEED/MIN_FIRE_INTERVAL_MS와 동형 — 디자이너 빈도 조정값이 아닌
   * 안전 가드라 코드 상수(데이터 외부화 제외). (구 lifeTimeMs 모듈/등급 스탯을 대체 — 플레이어 비대면.)
   */
  projectileSafetyCapMs: 30000,
} as const;

/**
 * 모듈 조립 그리드 (Pipe Mania식) 설정.
 * 전체 그리드 중 일부만 초기 해금. Start/Fire 고정, 골드로 인접 칸 점진 해금.
 */
export const MODULE_GRID = {
  width: 7,
  height: 3,
  /** 전투 탭에서 한 번에 보이는 세로 줄 수. 우주선별 모듈 그리드는 최대 7x3 표시를 기준으로 한다. */
  visibleRows: 3,
  /** Start 타일 (좌측 외부, 상단에서 두 번째 행). */
  start: { x: -1, y: 1 },
  /** Fire 타일 (우측 외부, 상단에서 두 번째 행) — 저장/데이터 호환 endpoint. 전투 발동은 Start 연결망 기준. */
  fire: { x: 7, y: 1 },
  /** 초기 해금 영역 (cols x0..x0+w-1, rows y0..y0+h-1). Start/Fire 행 7칸만 해금. */
  initialUnlock: { x0: 0, y0: 1, w: 7, h: 1 },
  /** 칸 해금 기본 비용 (global.tsv gridUnlockBaseCost). 현재 규칙은 multiplier=1로 모든 칸 균일 비용. */
  unlockBaseCost: r.getInt(K.gridUnlockBaseCost),
  /** 칸 해금 시마다 곱해지는 비용 배수 (global.tsv gridUnlockCostMultiplier, 1 = 균일 비용). */
  unlockCostMultiplier: r.getInt(K.gridUnlockCostMultiplier),
  /** 셀 픽셀 크기 + 간격 (탭 콘텐츠 영역에 맞춰 축소). 44→35로 약 80% 축소 — 그리드 폭이 줄면
   *  battleTab 팔레트(fill 영역)가 자동 확대된다(battleTabLayout이 이 값에서 그리드 박스 파생). */
  cellSize: 35,
  cellGap: 4,
} as const;

/** 화면 레이아웃 영역 (상단 HUD / 전투 필드 / 탭 콘텐츠 / 하단 탭바). */
export const LAYOUT = {
  /** 주요 UI 프레임(게임뷰 외곽선·탭 콘텐츠 패널·HUD·탭바)의 화면 좌우 여백(px). 모든 프레임이 이 값을 x 기준으로 파생. */
  frameInsetX: 10,
  hudY: 12,
  /** 탭 콘텐츠 영역 시작 y (전투 필드 아래). 전투뷰를 한 칸 정도 더 키우기 위해 탭 패널 시작점을 아래로 조정. */
  tabContentTop: 408,
  /** 탭 콘텐츠 배경 패널(NineSlicePanel) 상단 y. = tabContentTop - 7. 콘텐츠 inset의 SSoT. */
  tabPanelTop: 401,
  /** 탭 콘텐츠 배경 패널 높이. tabPanelTop + 이 값 = 패널 하단(773). 전투뷰 확장분만큼 탭 패널을 낮춘다. */
  tabPanelHeight: 372,
  /** 탭 콘텐츠 배경 패널 하단 y (= tabPanelTop + tabPanelHeight). 콘텐츠는 이 값을 넘지 않는다(tabBarTop=781과 8px 간격). */
  tabPanelBottom: 773,
  /** 게임뷰 배경 상단 y — HUD topbar(hudY+36) 아래 + 4px 간격. tileSprite·cover의 시작점. */
  gameviewTop: 52,
  /** 하단 탭바 행 높이 (2행 배치 — 기존 32px에서 약 10% 추가 축소). */
  tabBarHeight: 29,
  /** 탭바 행 간격. */
  tabBarRowGap: 3,
  /** 탭바 전체 상단 y — 탭 콘텐츠 영역의 하한(이 값 위까지). = tabBarY - (tabBarHeight*2 + tabBarRowGap)/2. */
  tabBarTop: 781,
  /** 하단 탭바 전체 세로 중심 y. */
  tabBarY: 811.5,
} as const;

/**
 * 디자인(world) 해상도 — Phaser FIT 기준(createGame DESIGN_WIDTH/HEIGHT의 SSoT).
 * safe-area inset 환산과 레이아웃 압축의 기준 크기.
 */
export const DESIGN = { width: 390, height: 844 } as const;

/** {@link deriveShellLayout} 출력 — LAYOUT 세로 좌표 + 발사대 y를 safe-area 반영해 묶은 셋. */
export interface ShellLayout {
  frameInsetX: number;
  hudY: number;
  gameviewTop: number;
  tabContentTop: number;
  tabPanelTop: number;
  tabPanelHeight: number;
  tabPanelBottom: number;
  tabBarHeight: number;
  tabBarRowGap: number;
  tabBarTop: number;
  tabBarY: number;
  launcherY: number;
}

/**
 * 하단 탭바가 홈 인디케이터 영역을 침범하지 않게 하는 bottom safe-area 상한(world px). iPhone 15 계열
 * safe-area bottom(34)은 그대로 유지하고, 그보다 큰 비정상/커스텀 값만 34로 제한한다. Flip6(24) 이하는
 * 기존처럼 불변이다. GameScene이 read()한 inset에 적용해 deriveShellLayout·게임뷰·벽돌이 동일한 inset을 쓴다.
 */
export const MAX_BOTTOM_INSET = 34;

/** {@link MAX_BOTTOM_INSET} 초과 bottom inset을 cap — 탭바~화면하단 여백 상한. top/left/right 불변. */
export function capBottomInset(insets: SafeAreaInsets): SafeAreaInsets {
  return insets.bottom > MAX_BOTTOM_INSET ? { ...insets, bottom: MAX_BOTTOM_INSET } : insets;
}

/**
 * 상단 배너 예약 높이 fallback(world px) — 웹 배너 미리보기와 native 측정값을 산출할 수 없는 경우에만
 * 사용한다. 실제 Android LevelPlay 배너는 `gameSceneBannerReserve.resolveBannerReserveWorldPx`가 CSS 폭, 50dp
 * 배너 높이, status bar fallback을 world px로 환산해 기기별 reserve를 계산한다.
 * 광고제거(adFree)·웹·기능 off면 reserve 0(shouldShowBanner)이라 기존 레이아웃과 항등.
 */
export const BANNER_RESERVED_WORLD_PX = 48;

/**
 * safe-area inset(world 단위) + 동적 design 높이를 반영한 화면 좌표 파생.
 * - **동적 높이 흡수(extra)**: `designHeight`(화면 비율 추종, {@link resolveDesignHeight})가 기준
 *   844보다 크면 그 여분 `extra`를 **게임뷰(상단 전투) 영역에만** 더하고, 하단 블록(탭 패널·탭바)은
 *   `extra`만큼 평행이동(끝정렬)한다. → 게임뷰가 늘어난 세로를 흡수해 길쭉한 기기(Flip6 22:9 등)의
 *   FIT 레터박스를 제거하고, 탭바~화면하단 간격은 기준과 동일하게 유지(탭바가 홈바 가까이).
 * - 세로: 가용 영역 [top, designHeight−bottom]으로 **비례 압축**(sY) — LAYOUT의 모든 선형 불변식을
 *   보존한다(tabPanelBottom=tabPanelTop+height, tabBarTop=tabBarY−(barH·2+gap)/2). 노치/홈
 *   인디케이터를 피해 전체 UI를 안전 영역 안으로.
 * - 가로: 좌우 inset만큼 시프트(sX). portrait(left=right=0)에선 무변(풀폭 유지).
 *
 * `insets`가 0이고 `designHeight`가 기본값(844)이면 LAYOUT 상수와 **정확히 동일**(extra=0 → 데스크톱·
 * 노치 없는 기기·일반 폰 회귀 0). 발사대 y도 게임뷰 bottom을 따라 +extra 후 같은 sY로 매핑한다.
 */
export function deriveShellLayout(
  insets: SafeAreaInsets,
  designHeight: number = DESIGN.height,
  bannerReservePx: number = 0,
): ShellLayout {
  // 길쭉 기기 흡수분. designHeight<844(직접 호출)면 음수 extra가 하단 블록을 위로 올리는 조용한
  // 오작동을 내므로 0으로 clamp — 프로덕션은 resolveDesignHeight clamp 하한 844라 항상 ≥0.
  const extra = Math.max(0, designHeight - DESIGN.height);
  // 모바일 상단 배너 reserve를 top inset처럼 가산 — 게임 콘텐츠가 배너에 가려지지 않게 전체 UI를
  // 아래로 + 세로 압축(화면 비율 이중화). bannerReservePx=0(adFree/웹/기능off)이면 기존과 항등.
  const topInset = insets.top + bannerReservePx;
  const sY = (designHeight - topInset - insets.bottom) / designHeight;
  const sX = (DESIGN.width - insets.left - insets.right) / DESIGN.width;
  const mapY = (y: number): number => topInset + y * sY;
  // 배너가 있으면 기존 웹/데스크톱용 최상단 여백(hudY=12)을 배너 하단 뒤에 또 두지 않는다.
  // reserve는 native 배너 높이를 이미 포함하므로 HUD 프레임은 배너 바로 아래에서 시작해야 한다.
  const topChromeOffset = bannerReservePx > 0 ? LAYOUT.hudY : 0;
  const mapTopChromeY = (y: number): number => topInset + Math.max(0, y - topChromeOffset) * sY;
  const mapH = (h: number): number => h * sY;
  return {
    frameInsetX: insets.left + LAYOUT.frameInsetX * sX,
    // 상단 고정 — HUD·게임뷰 상단은 extra 무관(여분은 게임뷰 bottom 쪽으로 흡수).
    hudY: mapTopChromeY(LAYOUT.hudY),
    gameviewTop: mapTopChromeY(LAYOUT.gameviewTop),
    // 게임뷰 bottom = tabContentTop. +extra로 게임뷰 높이만 늘려 여분 흡수.
    tabContentTop: mapY(LAYOUT.tabContentTop + extra),
    // 하단 블록(탭 패널·탭바) — +extra 평행이동(끝정렬). 화면 하단과의 간격 불변.
    tabPanelTop: mapY(LAYOUT.tabPanelTop + extra),
    tabPanelHeight: mapH(LAYOUT.tabPanelHeight),
    tabPanelBottom: mapY(LAYOUT.tabPanelBottom + extra),
    tabBarHeight: mapH(LAYOUT.tabBarHeight),
    tabBarRowGap: mapH(LAYOUT.tabBarRowGap),
    tabBarTop: mapY(LAYOUT.tabBarTop + extra),
    tabBarY: mapY(LAYOUT.tabBarY + extra),
    // 발사대는 게임뷰 bottom을 따라 +extra.
    launcherY: mapY(COMBAT.launcherY + extra),
  };
}
